<script lang="ts" setup>
import {
  Cell,
  CellGroup,
  Checkbox,
  Field,
  Popup,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  Switch,
} from "vant";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, ref, watch } from "vue";
import { useChatSession } from "@/stores/chatSession";
import UserAvatar from "@/components/UserAvatar.vue";
import DeleteIcon from "@/components/icon/deleteIcon.vue";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { MediaUploadFileItem } from "@/types/component";
import { Mate, SessionMode } from "@/types/entity";
import { WaitingSessionCreationInstance } from "@/components/mobile/chat/WaitingSessionCreation.vue";
import i18n from "@/locales";
import { useRouter } from "vue-router";
import { goChatMateList } from "@/components/mobile/chat/chatUtils";
import { isMobile } from "@/modules/common";
import { useTranslate } from "@/stores/translate";
import { ModelEnum, ModelList } from "@/enums/chatEnums";
import { useGlobalUser } from "@/stores/globalUser";
import { useTone } from "@/stores/tone";
import { onMounted } from "vue";

export type ChatMode = "text" | "scene";

const props = defineProps<{
  show: boolean;
  sessionId: number;
  targetMate: Mate;
  session?: MateSession;
}>();
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

const CHAT_MODE: { text: string; value: SessionMode }[] = [
  { text: i18n.global.t("Texts.textMode"), value: 0 },
  { text: i18n.global.t("Texts.sceneMode"), value: 1 },
];

const sessionStore = useChatSession();
const translateStore = useTranslate();
const user = useGlobalUser();
const router = useRouter();
const toneStore = useTone();
const session = computed(() => sessionStore.sessionMap[props.sessionId]);
const sessionTitle = ref(session.value.title);
const sessionAvatarMedia = ref<{
  mediaId: number | undefined;
  url: string;
} | null>(
  session.value.media
    ? {
        mediaId: session.value.media.id,
        url: session.value.media.url,
      }
    : null
);
const showMediaSelection = ref(false);
const waitingRef = ref<WaitingSessionCreationInstance | null>(null);
const showModelList = ref(false);
const currentModel = computed(() => {
  const id = session.value.model;
  return ModelList.find((model) => model.value === id) || {};
});
// Lifecycle
watch(
  () => props.show,
  () => {
    if (props.show) {
      // 从store中重置数据
      sessionTitle.value = session.value.title;
      sessionAvatarMedia.value = session.value.media
        ? {
            mediaId: session.value.media.id,
            url: session.value.media.url,
          }
        : null;
    }
  }
);

// Event Handler
const onChangeThumbnail = async (mediaList: MediaUploadFileItem[]) => {
  sessionAvatarMedia.value = {
    mediaId: mediaList[0].mediaId,
    url: mediaList[0].url!,
  };
};

// const onConfirm = async () => {
//   const loading = showLoadingToast({
//     message: i18n.global.t("Message.saving"),
//   });
//   try {
//     await sessionStore.updateSession(props.sessionId, {
//       title: sessionTitle.value.length > 0 ? sessionTitle.value : undefined,
//       // mediaId: sessionAvatarMedia.value?.mediaId,
//     });
//     loading.close();
//     showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
//     emit("update:show", false);
//   } finally {
//     loading.close();
//   }
// };

const onAutoPlayAudioChange = async (value: boolean) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.saving"),
  });
  try {
    await sessionStore.updateSession(props.sessionId, {
      autoPlayAudio: value ? 1 : 0,
    });
    loading.close();
    showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
  } catch (e) {
    loading.close();
  }
};

const deleteChatSession = async () => {
  await showConfirmDialog({
    title: i18n.global.t("ConfirmModal.deleteCurrentChatTitle"),
    message: i18n.global.t("ConfirmModal.deleteCurrentChatInfo"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  });
  const loading = showLoadingToast({
    message: i18n.global.t("Message.deleting"),
  });
  try {
    await sessionStore.deletePrivateSession(
      props.targetMate.id!,
      props.sessionId
    );
    loading.close();
    showSuccessToast({ message: i18n.global.t("Message.deleteSuccessful") });
    await goChatMateList(props.targetMate.uuid, true);
  } finally {
    loading.close();
  }
};

const onChangeSessionMode = async (mode: SessionMode) => {
  if (mode === 1) {
    await waitingRef.value?.enterSceneMode(props.sessionId, props.targetMate);
    return;
  }
  const loading = showLoadingToast({
    message: i18n.global.t("Message.switchingChatMode"),
  });
  try {
    await sessionStore.updateSessionMode(
      props.sessionId,
      props.targetMate,
      mode
    );
    loading.close();
    showSuccessToast({ message: i18n.global.t("Message.switchSuccessful") });
  } catch (e) {
    loading.close();
  }
};
const onChangeModel = async (model: ModelEnum) => {
  const loading = showLoadingToast(i18n.global.t("Message.saving"));
  try {
    const targetModel = ModelList.find((item) => item.value === model);
    await sessionStore.updateSession(props.sessionId, {
      model: model,
      isNsfw: targetModel?.isNsfwSupport ? 1 : 0,
    });
    loading.close();
    showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
  } catch (e) {
    loading.close();
  }
};

onMounted(() => {
  toneStore.getToneList();
});
</script>
<template>
  <popup
    :position="isMobile() ? 'bottom' : 'right'"
    :show="show"
    class="max-h-[80%] w-full lg:max-h-full lg:h-full grid grid-rows-[auto_1fr] lg:w-[30rem]"
    round
    teleport="body"
    @update:show="emit('update:show', $event)"
  >
    <threads-nav show-back>
      <template #left>
        <button
          class="btn btn-ghost btn-sm btn-square"
          @click="emit('update:show', false)"
        >
          <i class="iconify mingcute--left-line" />
        </button>
      </template>
      <template #center>
        <span>{{ $t("User.mate.chat.chatSettings") }}</span>
      </template>
      <template #right>
        <div class="flex items-center justify-end">
          <!--          <button class="btn btn-sm btn-primary" @click="onConfirm">-->
          <!--            {{ $t("Buttons.confirm") }}-->
          <!--          </button>-->
        </div>
      </template>
    </threads-nav>
    <div class="overflow-y-auto flex flex-col gap-4">
      <!-- <div>
        <cell-group :title="$t('Chats.basicSettings')" inset>
          <field
            v-if="false"
            v-model="sessionTitle"
            :placeholder="targetMate.name"
            autosize
            label-align="top"
            rows="1"
            type="textarea"
          >
            <template #label>
              <div class="flex flex-col">
                <span>{{ $t("Chats.name") }}</span>
                <span class="text-xs text-base-content/50">{{
                  $t("Chats.nameTip")
                }}</span>
              </div>
            </template>
          </field>
          <cell
            :title="$t('User.model')"
            clickable
            is-link
            @click="showModelList = true"
          >
            <template #icon>
              <div class="flex items-center mr-1">
                <i class="iconify mingcute--box-3-line" />
              </div>
            </template>
            <span>
              {{ currentModel!.label }}
            </span>
          </cell>
          <cell
            v-if="false"
            :title="$t('Chats.thumbnail')"
            center
            is-link
            @click="showMediaSelection = true"
          >
            <template #value>
              <div class="flex items-center justify-end">
                <user-avatar
                  :avatar-url="sessionAvatarMedia?.url"
                  class="w-10"
                />
              </div>
            </template>
          </cell>
        </cell-group>
      </div> -->
      <div v-if="!user.isHideNSFW" class="px-4 flex flex-col gap-2">
        <div class="flex justify-between items-center">
          <div class="text-base-content/50">
            {{ $t("User.mate.module.persona") }}
          </div>
          <button
            v-if="session.matePersona"
            class="btn btn-xs btn-ghost btn-square"
            @click="sessionStore.showPersonaSelectPopup = true"
          >
            <i class="iconify mingcute--edit-line" />
          </button>
        </div>
        <PersonaCard
          v-if="session.matePersona && targetMate"
          :mate-persona="session.matePersona"
          :target-mate="targetMate"
        />
        <div
          v-else-if="!targetMate?.isAllowedInjected"
          class="badge badge-warning"
        >
          {{ $t("Message.notAllowInjection") }}
        </div>
        <AuthLock v-else>
          <div
            class="border w-full h-28 rounded border-dashed flex flex-col gap-2 items-center justify-center text-base-content/50 cursor-pointer"
            @click="sessionStore.showPersonaSelectPopup = true"
          >
            <i class="iconify mingcute--add-circle-line" />
            <div class="text-sm">
              {{ $t("User.mate.module.persona") }}
            </div>
          </div>
        </AuthLock>
      </div>
      <div class="px-4 flex flex-col gap-2">
        <div class="text-base-content/50">
          {{ $t("User.mate.chat.scenario") }}
        </div>
        <SceneCard
          v-if="session.chatScene && targetMate"
          :chat-scene="session.chatScene"
          :targetMate="targetMate!"
        />
        <SceneCard
          v-else-if="targetMate"
          :chat-scene="{
            media: targetMate?.sceneMedia ?? {
              url: targetMate?.mediaUrl!,
            },
            scenario: targetMate?.scenario,
            greeting: targetMate?.greeting,
          }"
          :target-mate="targetMate!"
        />
      </div>
      <div>
        <cell-group :title="$t('Chats.selectMateTone')" inset>
          <cell
            :title="session.mateToneId ?
          toneStore.toneMap[session.mateToneId!]?.name :
          $t(`game.show.unselected`)"
            clickable
            is-link
            @click="sessionStore.openSelectToneModal('mate')"
          />
        </cell-group>
      </div>

      <!-- <div>
        <cell-group :title="$t('Chats.selectNarratorTone')" inset>
          <cell
            :title="session.narratorToneId ?
          toneStore.toneMap[session.narratorToneId!]?.name :
          $t(`game.show.unselected`)"
            clickable
            is-link
            @click="clickSelectTone('narrator')"
          />
        </cell-group>
      </div> -->

      <div>
        <cell-group :title="$t('Chats.assistantFeature')" inset>
          <cell
            :title="$t('Chats.translateLanguage')"
            clickable
            is-link
            @click="translateStore.showLangSelection = true"
          >
            <template #icon>
              <div class="flex items-center mr-1">
                <i class="iconify mdi--language text-xs" />
              </div>
            </template>
            <span>
              {{ $t(`translate['lang']['${translateStore.targetLang}']`) }}
            </span>
          </cell>
        </cell-group>
      </div>
      <div v-if="false">
        <cell-group :title="$t('Chats.chatMode')" inset>
          <cell
            v-for="(item, idx) in CHAT_MODE"
            :key="idx"
            :title="item.text"
            clickable
            @click.stop="onChangeSessionMode(item.value)"
          >
            <template #right-icon>
              <Checkbox :model-value="session.mode === item.value" />
            </template>
          </cell>
        </cell-group>
      </div>
      <div v-if="false">
        <cell-group :title="$t('Chats.audio')" inset>
          <cell :title="$t('Chats.audioAutoPlay')">
            <template #right-icon>
              <Switch
                :disabled="targetMate.mateToneId === 0"
                :model-value="session?.autoPlayAudio === 1"
                class="border border-base-content"
                size="16px"
                @update:model-value="onAutoPlayAudioChange"
              />
            </template>
          </cell>
        </cell-group>
      </div>
      <div class="p-4">
        <button class="btn w-full btn-sm btn-error" @click="deleteChatSession">
          <delete-icon class="icon-base" />
          {{ $t("Chats.deleteCurrentChat") }}
        </button>
      </div>
    </div>
  </popup>
  <media-select-popup
    v-model:show="showMediaSelection"
    :max-count="1"
    :multiple="false"
    :upload-type="3"
    media-type="img"
    replace
    @on-media-selected="onChangeThumbnail"
  />
  <waiting-session-creation ref="waitingRef" />
  <ModelSelectionPopup
    v-model:show="showModelList"
    :initial-model="session?.model"
    @confirm="onChangeModel"
  />

  <ToneSelectionPopup
    v-model:show="sessionStore.showSelectToneModal"
    :selected-tone-id="
      sessionStore.selectToneType === 'narrator'
        ? session.narratorToneId
        : session.mateToneId
    "
    :gender="targetMate.gender || 0"
    @update:selected-tone-id="
      (toneId) => sessionStore.onUpdateCharacterTone(sessionId, toneId)
    "
  />
</template>
